import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { VALUE_PROPS, SERVICES } from "@/data/content";

const HERO_IMG = "/images/hero-skyline.jpg";
const MISSION_IMG = "/images/vauxhall-portrait.jpg";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="London skyline at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-[#0A0A0B]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-20">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-3 border border-[#C9A24B]/40 rounded-full px-5 py-2 mb-8">
              <span className="text-xs tracking-[0.25em] uppercase text-[#C9A24B] font-medium">
                Understand · Advise · Protect
              </span>
            </div>

            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#F5F5F0]">
              Welcome to <br />
              <span className="text-[#C9A24B]">Blackthorne Stratton</span>
            </h1>

            <p className="mt-8 text-base sm:text-lg text-[#A1A1AA] font-light leading-relaxed max-w-xl">
              Specialist risk, intelligence and advisory services, delivering discreet solutions to
              private clients, corporate organisations and high-value environments globally.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="group inline-flex items-center justify-center gap-2 bg-[#C9A24B] text-black px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#E5E4E2] transition-colors duration-300"
              >
                Our Services
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-cta"
                className="inline-flex items-center justify-center gap-2 border border-[#C9A24B]/50 text-[#C9A24B] px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#C9A24B]/10 transition-colors duration-300"
              >
                Make an Enquiry
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">
            <span>Intelligence-led</span><span className="text-[#C9A24B]">·</span>
            <span>Discreet</span><span className="text-[#C9A24B]">·</span>
            <span>Professional</span><span className="text-[#C9A24B]">·</span>
            <span>Trusted</span>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-24 sm:py-32 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-6">What we do</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F0] max-w-2xl">
              More than physical protection alone.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUE_PROPS.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="group h-full bg-[#121214] border border-[#27272A] p-8 lg:p-10 hover:border-[#C9A24B]/50 transition-colors duration-500">
                  <v.icon size={36} strokeWidth={1} className="text-[#C9A24B]" />
                  <h3 className="mt-6 font-serif text-2xl text-[#F5F5F0]">{v.title}</h3>
                  <p className="mt-3 text-[#A1A1AA] font-light leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="py-24 sm:py-32 bg-[#08080A] border-y border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                <SectionLabel className="mb-6">Our expertise</SectionLabel>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F0] max-w-2xl">
                  A complete spectrum of protective &amp; intelligence services.
                </h2>
              </div>
              <Link
                to="/services"
                data-testid="services-teaser-link"
                className="group inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase text-[#C9A24B] shrink-0"
              >
                View all services
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block h-full bg-[#121214] border border-[#27272A] p-8 hover:border-[#C9A24B]/50 hover:-translate-y-1 transition-all duration-500"
                >
                  <s.icon size={32} strokeWidth={1} className="text-[#C9A24B]" />
                  <h3 className="mt-6 font-serif text-2xl text-[#F5F5F0] group-hover:text-[#C9A24B] transition-colors">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[#A1A1AA] font-light leading-relaxed">{s.summary}</p>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-12 text-center text-sm text-[#71717A] font-light">
              Our capabilities extend beyond those listed — bespoke mandates are discussed in confidence on consultation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION TEASER */}
      <section className="py-24 sm:py-32 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionLabel className="mb-6">Our mission</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F0] leading-tight">
              Effective protection begins with understanding.
            </h2>
            <p className="mt-8 text-[#A1A1AA] font-light leading-relaxed text-lg">
              Every client, environment and threat profile is different. Our services are designed
              around intelligence, preparation and discretion, rather than visible force. Our
              personnel combine operational experience with a professional consultancy mindset.
            </p>
            <Link
              to="/mission"
              data-testid="mission-teaser-link"
              className="group mt-10 inline-flex items-center gap-2 border border-[#C9A24B]/50 text-[#C9A24B] px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#C9A24B]/10 transition-colors duration-300"
            >
              Discover our approach
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={MISSION_IMG} alt="Advisory professional" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/70 to-transparent" />
              </div>
              <div className="absolute -bottom-px -left-px border-l-2 border-b-2 border-[#C9A24B] w-20 h-20" />
              <div className="absolute -top-px -right-px border-r-2 border-t-2 border-[#C9A24B] w-20 h-20" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-32 bg-[#08080A] border-t border-[#27272A] grain overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F0]">
              Discuss your requirements in confidence.
            </h2>
            <p className="mt-6 text-[#A1A1AA] font-light text-lg max-w-2xl mx-auto">
              Whether you are facing a complex risk, a sensitive matter, or seeking to strengthen your
              resilience — our team is ready to advise.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="group mt-10 inline-flex items-center gap-2 bg-[#C9A24B] text-black px-10 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#E5E4E2] transition-colors duration-300"
            >
              Contact us
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
